import { AnimatePresence } from "framer-motion";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { arrowUpRight as Arrow } from "../../assets/styledSvg/arrowUpRight";
import { ReactComponent as Check } from "../../assets/svg/checkmark.svg";
import { ReactComponent as Close } from "../../assets/svg/close.svg";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { ReactComponent as Edit } from "../../assets/svg/pencil.svg";
import ClaimRewardsModal from "../../components/ClaimRewardsModal";
import BackgroundVideoFX from "../../components/shared/BackgroundVideoFX/BackgroundVideoFX";
import { Select } from "../../components/shared/Select";
import Transition from "../../components/shared/Transition";
import { LINKS, REWARD_LINKS } from "../../constants/links";
import { AccountStateEnum, AuthContext } from "../../contexts/AuthContext";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useUpdateUsername } from "../../hooks/api/account/useUpdateUsername";
import { useReferralStatistics } from "../../hooks/api/referrals/useReferralStatistics";
import { useToast } from "../../hooks/toast";
import { copyStringToClipboard } from "../../utils/strings";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { ReferralCodes } from "./ReferralCodes";
import { ReferralStats } from "./ReferralStats";
import { ReferralTable } from "./ReferralTable";
import { RewardTable } from "./RewardTable";
import {
  ReferralTableKey,
  getReferralTables,
  isReferralRewardsEnabled,
} from "./referralTabs";
import { Button, ButtonThemeEnum } from "../../components/Buttons/styles";
import { useUrlParamState } from "../../hooks/useUrlParamState";
import {
  Container,
  HeaderContainer,
  HeaderTitle,
  ReferralLink,
  ReferralLinkWrapper,
  ReferralUsernameEditForm,
  ReferralUsernameInput,
  TableContainer,
  VideoContainer,
} from "./style";

/** A placeholder "#" is not a destination. */
const hasReferralProgramLink = /^https?:\/\//.test(REWARD_LINKS.referralClaims);

interface IReferralsTabProps {
  isMobileScreen?: boolean;
}

export function ReferralsTab({ isMobileScreen }: IReferralsTabProps) {
  const rewardsEnabled = isReferralRewardsEnabled();
  const [selectedTable, setTable] = useUrlParamState<ReferralTableKey>(
    "tab",
    "referral",
    getReferralTables()
  );
  const [showRewardsModal, setShowRewardsModal] = useState<boolean>(false);
  const { data: accountData } = useGetAccount();
  const updateUsername = useUpdateUsername();
  const { accountApiKeyState, account } = useContext(AuthContext);
  const { data: referralStatsData } = useReferralStatistics();
  const { addToast } = useToast();
  const { t } = useTranslation("app", {
    keyPrefix: "pages.ReferralsTab.ReferralsTab",
  });

  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [isSavingUsername, setIsSavingUsername] = useState(false);

  const startEditUsername = useCallback(() => {
    setUsernameDraft(accountData?.username ?? "");
    setIsEditingUsername(true);
  }, [accountData?.username]);

  const cancelEditUsername = useCallback(() => {
    setIsEditingUsername(false);
    setUsernameDraft("");
  }, []);

  const saveUsername = useCallback(async () => {
    const trimmed = usernameDraft.trim();
    if (!trimmed || trimmed === accountData?.username || isSavingUsername) return;
    setIsSavingUsername(true);
    try {
      await updateUsername(trimmed);
      setIsEditingUsername(false);
      setUsernameDraft("");
      addToast({
        type: ToastEnum.SIMPLE,
        header: "Username updated successfully",
        status: ToastStatusEnum.SUCCESS,
      });
    } catch (e) {
      addToast({
        type: ToastEnum.SIMPLE,
        header: (e as Error)?.message || "Failed to update username",
        status: ToastStatusEnum.ERROR,
      });
    } finally {
      setIsSavingUsername(false);
    }
  }, [usernameDraft, accountData?.username, isSavingUsername, updateUsername, addToast]);

  const onCopy = useCallback(() => {
    if (accountData?.username) {
      copyStringToClipboard(`${LINKS.app}/r/${accountData.username}`);
      addToast({
        type: ToastEnum.SIMPLE,
        header: t("referral_link_copied"),
        status: ToastStatusEnum.SUCCESS,
      });
    }
  }, [accountData, addToast, t]);

  return (
    <>
      <ClaimRewardsModal
        show={rewardsEnabled && showRewardsModal}
        onHide={() => setShowRewardsModal(false)}
        data={referralStatsData}
      />
      <Container
        $isMobileScreen={isMobileScreen}
        transition={{
          duration: 0.25,
          ease: "easeInOut",
        }}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
      >
        <VideoContainer isMobile={isMobileScreen}>
          <BackgroundVideoFX />
        </VideoContainer>
        <HeaderContainer $isMobileScreen={isMobileScreen}>
          <HeaderTitle>{t("referrals")}</HeaderTitle>
          <span>
            {t("referrals_desc_1")}
            {/* No program-details page is published yet: REWARD_LINKS still
                holds the "#" placeholder, and the link opened an empty tab.
                It reappears once a real URL is configured there. */}
            {hasReferralProgramLink && (
              <>
                <br />
                <a
                  href={REWARD_LINKS.referralClaims}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("referrals_desc_2")}
                  <Arrow />
                </a>
              </>
            )}
          </span>
          <ReferralStats isMobileScreen={isMobileScreen} />
          <ReferralLinkWrapper $isMobileScreen={isMobileScreen}>
            <ReferralLink>
              {account && accountApiKeyState === AccountStateEnum.OK ? (
                isEditingUsername ? (
                  <ReferralUsernameEditForm
                    onSubmit={(e) => {
                      e.preventDefault();
                      void saveUsername();
                    }}
                  >
                    <ReferralUsernameInput
                      autoFocus
                      type="text"
                      value={usernameDraft}
                      maxLength={32}
                      disabled={isSavingUsername}
                      onChange={(e) => setUsernameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") cancelEditUsername();
                      }}
                    />
                  </ReferralUsernameEditForm>
                ) : (
                  <p>
                    {window.location.origin}/r/<span>{accountData?.username}</span>
                  </p>
                )
              ) : (
                <p>-</p>
              )}
              {account && accountApiKeyState === AccountStateEnum.OK && (
                isEditingUsername ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void saveUsername()}
                      disabled={
                        isSavingUsername ||
                        !usernameDraft.trim() ||
                        usernameDraft.trim() === accountData?.username
                      }
                      aria-label={t("save_username")}
                    >
                      <Check />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditUsername}
                      disabled={isSavingUsername}
                      aria-label={t("cancel_edit_username")}
                    >
                      <Close />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEditUsername}
                    aria-label={t("edit_username")}
                  >
                    <Edit />
                  </button>
                )
              )}
              <button type="button" onClick={onCopy}>
                <Copy />
              </button>
            </ReferralLink>

            {rewardsEnabled && account && accountApiKeyState === AccountStateEnum.OK && (
              <Button
                fullWidth
                buttonTheme={ButtonThemeEnum.PRIMARY}
                onClick={() => setShowRewardsModal(true)}
                disabled={
                  !referralStatsData ||
                  Number(referralStatsData?.total_rewards_unclaimed) === 0
                }
              >
                {t("claim_rewards")}
              </Button>
            )}
          </ReferralLinkWrapper>
        </HeaderContainer>
        {account && accountApiKeyState === AccountStateEnum.OK && (
          <ReferralCodes isMobileScreen={isMobileScreen} />
        )}
        <TableContainer>
          <Select
            isRound
            options={[
              ...(rewardsEnabled
                ? [
                    {
                      label: t("rewards_history"),
                      isActive: selectedTable === "reward",
                      onClick: () => setTable("reward"),
                    },
                  ]
                : []),
              {
                label: t("referral_history"),
                isActive: selectedTable === "referral",
                onClick: () => setTable("referral"),
              },
            ]}
          />
          <AnimatePresence key={selectedTable}>
            <Transition>
              {selectedTable === "reward" ? (
                <RewardTable isMobileScreen={isMobileScreen} />
              ) : (
                <ReferralTable isMobileScreen={isMobileScreen} />
              )}
            </Transition>
          </AnimatePresence>
        </TableContainer>
      </Container>
    </>
  );
}
